import {
	CallType,
	DocumentTarget,
	SendDocumentType,
	StatusActualTimeType,
	StatusEstimateRequirement,
	StatusEstimateType,
	StatusRule,
	StatusType,
} from "__generated__/enums";
import z from "zod";

export type StatusFormInput = z.input<typeof StatusFormSchema>;
export type StatusFormOutput = z.output<typeof StatusFormSchema>;

export const ApplicationDefinedStatusSchema = <const T extends z.util.EnumLike,>(enumValue: T) => z.object({
	name: z.enum(enumValue),
	color: z.string(),
	languageKeyPrefix: z.string(),
});

export const StatusFormSchema = z.object({
	id: z.number().nullable(),
	type: z.enum(StatusType),
	cargoActionType: z.enum(CallType).nullable(),
	description: z.string().min(3).max(255),
	color: z.string(),
	requireActualTime: z.enum(StatusActualTimeType),
	estimateType: z.enum(StatusEstimateType).nullable(),
	estimateRequirement: z.enum(StatusEstimateRequirement),
	allowComment: z.boolean(),
	allowSkipperComment: z.boolean(),
	isInProgress: z.boolean(),
	isFinishedStatus: z.boolean(),
	isClosedStatus: z.boolean(),
	isCancelledStatus: z.boolean(),
	isExpiringStatus: z.boolean(),
	applyByCustomer: z.boolean(),
	applyBySkipper: z.boolean(),
	applyByTerminal: z.boolean(),
	isLoadedStatus: z.boolean(),
	isDischargedStatus: z.boolean(),
	voyageVisibleBySkipper: z.boolean(),
	voyageVisibleByCreditor: z.boolean(),
	voyageEditableBySkipper: z.boolean(),
	voyageEditableByCreditor: z.boolean(),
	callVisibleByLocationOwner: z.boolean(),
	isRejectStatus: z.boolean(),
	isAcceptStatus: z.boolean(),
	notifyShipOwners: z.boolean(),
	notifyShipManagers: z.boolean(),
	notifySkippers: z.boolean(),
	notifyOperators: z.boolean(),
	notifyCustomers: z.boolean(),
	notifyTerminals: z.boolean(),
	includeInNominationExport: z.boolean(),
	isCustomerNominatingStatus: z.boolean(),
	isSkipperNominatingStatus: z.boolean(),
	supplierRequired: z.boolean(),
	skipperRequiredFillInSailingReport: z.boolean(),
	skipperRequiredFillInWeightVolume: z.boolean(),
	shouldUpdateJDE: z.boolean(),
	shouldLoadOnTerminal: z.boolean(),
	laytimeStart: z.boolean(),
	laytimeEnd: z.boolean(),
	requireDocuments: z.boolean(),
	requireTanks: z.boolean(),
	successiveStatuses: z.array(z.object({
		id: z.number(),
		type: z.enum(StatusType),
		description: z.string(),
	})).nullable(),
	showConfirmMessage: z.boolean(),
	translations: z.object({
		id: z.number().nullable(),
		defaultDescription: z.string(),
		descriptions: z.record(z.string(), z.string().nullish()),
	}),
	confirmWindowTranslations: z.object({
		id: z.number().nullable(),
		defaultDescription: z.string(),
		descriptions: z.record(z.string(), z.string().nullish()),
	}),
	sendDocument: z.object({
		enabled: z.boolean(),
		type: z.enum(SendDocumentType).nullish(),
		target: z.enum(DocumentTarget).nullish(),
	}),
	rules: z.array(z.object({
		id: z.number().nullable(),
		ruleType: z.enum(StatusRule),
		targetStatus: z.object({
			id: z.number(),
			type: z.enum(StatusType),
			description: z.string(),
		}).nullable(),
		ifFitsStatusFlow: z.boolean(),
		ifNotAppliedBefore: z.boolean(),
	})),
});

export const DefaultStatusSettingsFormSchema = z.object({
	cargoDefault: z.number().nullable(),
	voyageDefault: z.number().nullable(),
	callDefault: z.number().nullable(),
	callVoyageInProgressDefault: z.number().nullable(),
	callAtaDefault: z.number().nullable(),
	callAtdDefault: z.number().nullable(),
	callAtnDefault: z.number().nullable(),
	callDtnDefault: z.number().nullable(),
	cargoPlannedDefault: z.number().nullable(),
	voyageOrderDefault: z.number().nullable(),
	afterDeclaringVoyage: z.number().nullable(),
	declarationDefault: z.number().nullable(),
	declarationDeclared: z.number().nullable(),
	invoiceDefault: z.number().nullable(),
});
